import React, { useState, useCallback } from 'react';
import { CreditCard, X } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface StripeCheckoutButtonProps {
  state: string;
  county: string;
}

export function StripeCheckoutButton({ state, county }: StripeCheckoutButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleComplete = useCallback(() => {
    console.log('Payment completed successfully!');
    setShowModal(false);
    setShowSuccessModal(true);
  }, []);

  const fetchClientSecret = useCallback(async () => {
    // Get auth token and user email from Amplify
    const { fetchAuthSession, fetchUserAttributes } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const userAttributes = await fetchUserAttributes();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    const apiUrl = import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${apiUrl}/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        amount: 667, // $667
        state: state,
        county: county,
        email: userAttributes.email || 'unknown@test.com',
        name: userAttributes.name || 'Test User',
        mode: 'embedded', // Use embedded mode for modal
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data.clientSecret;
  }, [state, county]);

  const handleOpenCheckout = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const displayCounty = county || 'Your County';
  const displayState = state || 'FL';

  return (
    <>
      {/* Success Confirmation Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative animate-in">
            {/* Success Icon Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 rounded-t-2xl text-center">
              <div className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Thank You!</h2>
              <p className="text-green-100">Your order has been confirmed</p>
            </div>

            {/* Modal Content */}
            <div className="p-8 space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{county}, {state}</h3>
                <p className="text-gray-600">Tax Delinquent Data - $667</p>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <div>
                      <p className="font-semibold text-blue-900">Confirmation Email Sent</p>
                      <p className="text-sm text-blue-700 mt-1">Check your inbox for order details and receipt</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                    </svg>
                    <div>
                      <p className="font-semibold text-purple-900">Need Help?</p>
                      <p className="text-sm text-purple-700 mt-1">Reply to the email or use the chat in your order details</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div>
                      <p className="font-semibold text-green-900">Delivery Timeline</p>
                      <p className="text-sm text-green-700 mt-1">Your list will be uploaded to the portal within <strong>7-10 business days</strong> (usually sooner)</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  // Scroll to My Orders section
                  setTimeout(() => {
                    const ordersSection = document.querySelector('[data-section="my-orders"]');
                    ordersSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 300);
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg"
              >
                View My Orders
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">

        {/* What You'll Receive */}
        <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📋 What You'll Receive:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              <span><strong>At least 300 records</strong> in {displayCounty}, {displayState} (if fewer available, we'll supplement)</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              <span><strong>Significantly delinquent properties only</strong> - owing $3,000+ for 2+ years</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              <span><strong>Residential and individual properties only</strong> - no trusts or LLCs</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✓</span>
              <span><strong>Estimated delivery:</strong> 7-10 days (usually sooner)</span>
            </li>
          </ul>
        </div>

        {/* Selection reminder (only show when incomplete) */}
        {(!state || !county) && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 text-center">
            <p className="text-sm font-semibold text-blue-800">
              👆 Select state and county above to enable checkout
            </p>
          </div>
        )}

        {/* Checkout Button */}
        <button
          onClick={handleOpenCheckout}
          disabled={loading || !state || !county}
          className="w-full flex items-center justify-center space-x-3 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-bold text-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-green-600 disabled:hover:to-green-700"
        >
          <CreditCard className="w-6 h-6" />
          <span>Purchase for $667</span>
        </button>

        {import.meta.env.VITE_ENVIRONMENT === 'dev' && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 text-center">
            <p className="text-xs font-bold text-yellow-800">⚠️ TEST MODE - Use card 4242 4242 4242 4242</p>
          </div>
        )}
      </div>

      {/* Modal Dialog */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>

            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Complete Your Purchase</h2>
              <p className="text-sm text-gray-600 mt-1">
                Tax Delinquent Records - {displayCounty}, {displayState}
              </p>
            </div>

            {/* Stripe Embedded Checkout */}
            <div className="p-6">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  fetchClientSecret,
                  onComplete: handleComplete
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
